import api from '@/lib/api'
import { COMPLETE, ERROR, IN_PROGRESS } from '@/lib/constants/actionStatuses'
import { PATH_PAGINATED_RESULT } from '@/lib/constants/api'
import { notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

/**
 * Factory that creates a Vuex action for fetching a paginated list from the API.
 *
 * The generated action follows the standard pattern used across all list modules:
 *   - show progress modal
 *   - GET /<path>/paginated with the provided filters
 *   - commit the result via setPaginatedMutation
 *   - notify on error
 *   - hide progress modal
 *
 * @param {string} path - API resource path constant (e.g. PATH_USERS)
 * @param {string} setPaginatedMutation - mutation name to commit the paginated result to
 * @param {string} errorMessage - user-facing error message shown on failure
 * @returns {function} Vuex action function ({ commit, dispatch }, filters) => Promise
 *
 * @example
 * // In a Vuex module:
 * actions: {
 *   getUsersPaginated: createPaginatedGetAction(PATH_USERS, 'setPaginatedUsers', 'Unable to get users')
 * }
 */
const createPaginatedGetAction = (path, setPaginatedMutation, errorMessage) => {
  return ({ commit, dispatch }, filters = {}) => {
    commit('updateStatus', { status: IN_PROGRESS })
    showProgressModal({ dispatch, message: `Getting ${path}, please wait...` })

    return api.doGet({ path: `${path}/${PATH_PAGINATED_RESULT}`, parameters: filters })
      .then(json => json.data)
      .then(paginatedResult => {
        commit('updateStatus', { status: COMPLETE })
        commit(setPaginatedMutation, paginatedResult)
        return paginatedResult.result
      })
      .catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, errorMessage)
        return []
      })
      .finally(() => {
        hideProgressModal({ dispatch })
      })
  }
}

export default createPaginatedGetAction
